import { partnerSupabase } from "Infrastructure/Supabase/partnerSupabaseClient";
import { useQuery } from "@tanstack/react-query";
import type { ProtocolRecord } from "./useOrderProtocolsQuery";

export interface PartnershipRecord {
	id: string;
	order_id: string;
	action_type: "connection" | "job_start" | "completion" | "examination";
	status: boolean;
	reason: string | null;
	note_for_adam: string | null;
	action: string;
	file_path: string | null;
	country: string | null;
	created_at: string;
}

export interface PartnershipStatus {
	connected: boolean;
	started: boolean;
	finished: boolean;
	examined: boolean;
}

export const PARTNERSHIP_RECORDS_KEY = "PARTNERSHIP_RECORDS";

export const usePartnershipRecordsQuery = (orderId: number | undefined) =>
	useQuery({
		queryKey: [PARTNERSHIP_RECORDS_KEY, orderId],
		queryFn: async () => {
			const { data, error } = await partnerSupabase
				.from("partnership_records")
				.select(
					"id, order_id, action_type, status, reason, note_for_adam, action, file_path, country, created_at",
				)
				.eq("order_id", String(orderId))
				.order("created_at", { ascending: true });

			if (error) {
				throw error;
			}

			return (data as PartnershipRecord[]) ?? [];
		},
		enabled: !!orderId,
		refetchInterval: 30_000,
	});

export function derivePartnershipStatus(
	records: PartnershipRecord[],
	protocols?: ProtocolRecord[],
): PartnershipStatus {
	const latest = new Map<string, PartnershipRecord>();

	for (const record of records) {
		const existing = latest.get(record.action_type);
		if (
			!existing ||
			new Date(record.created_at) > new Date(existing.created_at)
		) {
			latest.set(record.action_type, record);
		}
	}

	// Protocol-based status overrides Supabase for job_start and completion
	const startProtocol = protocols?.find((p) => p.type === "start");
	const handoverProtocol = protocols?.find((p) => p.type === "handover");

	return {
		connected: latest.get("connection")?.action === "connected",
		started:
			startProtocol?.status === "accepted" ||
			latest.get("job_start")?.action === "started",
		finished:
			handoverProtocol?.status === "accepted" ||
			latest.get("completion")?.action === "finished",
		examined: latest.get("examination")?.action === "examined",
	};
}
